import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { EnkaHeader } from "@/components/EnkaHeader";
import { api } from "@/lib/api";
import { usePersonnel } from "@/lib/personnelContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { STATUS_STYLES, PRIORITY_STYLES, composeLocation } from "@/lib/constants";
import { formatAge } from "@/lib/ticketUtils";
import {
  Loader2, LogOut, PlusCircle, CheckCircle2, XCircle, ClipboardList, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

function ConfirmDialog({ ticket, onClose, onConfirmed }) {
  const [note, setNote] = useState("");
  const [action, setAction] = useState(null); // 'close' | 'not_resolved'
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!ticket) { setNote(""); setAction(null); } }, [ticket]);
  if (!ticket) return null;

  const submit = async (act) => {
    setAction(act);
    setSaving(true);
    try {
      const { data } = await api.post(
        `/personnel/tickets/${ticket.id}/confirm`,
        { action: act, note: note.trim() || null },
      );
      onConfirmed(data);
      toast.success(act === "close" ? "Ticket closed. Thank you!" : "Reopened — the IT team was notified.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm");
    } finally { setSaving(false); setAction(null); }
  };

  return (
    <Dialog open={Boolean(ticket)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-[color:var(--enka-blue)]">
            {ticket.ticket_no}
          </DialogTitle>
          <DialogDescription>
            The IT team marked this request as resolved. Do you confirm?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-[color:var(--enka-muted)]">{ticket.category} · {ticket.area}</div>
          <div>
            <Label className="text-xs">Comment (optional)</Label>
            <Textarea
              rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a short note for the IT team..."
              data-testid="confirm-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button" variant="outline"
            onClick={() => submit("not_resolved")} disabled={saving}
            data-testid="confirm-not-resolved-btn"
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            {saving && action === "not_resolved" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <><XCircle className="h-4 w-4 mr-1.5" /> Not resolved</>
            )}
          </Button>
          <Button
            type="button" onClick={() => submit("close")} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-close-btn"
          >
            {saving && action === "close" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Close ticket</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MyTickets() {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = usePersonnel();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/personnel/my-tickets");
      setTickets(data);
    } catch { /* handled by interceptor */ } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetch(); }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const logout = () => { signOut(); navigate("/"); };

  return (
    <div className="min-h-screen flex flex-col" data-testid="my-tickets-page">
      <EnkaHeader
        subtitle={`Signed in as ${user.full_name}`}
        rightSlot={
          <button
            type="button" onClick={logout}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
            data-testid="my-tickets-logout"
          >
            <LogOut className="h-3 w-3" />
            <span>Sign out</span>
          </button>
        }
      />

      <main className="flex-1 mx-auto w-full max-w-2xl px-5 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display text-2xl text-[color:var(--enka-ink)] flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[color:var(--enka-blue)]" /> My tickets
            </h1>
            <p className="text-xs text-[color:var(--enka-muted)]">
              {tickets.length} ticket{tickets.length === 1 ? "" : "s"} · @{user.username}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button" onClick={fetch}
              className="inline-flex items-center gap-1.5 text-xs text-[color:var(--enka-muted)] hover:text-[color:var(--enka-blue)]"
              data-testid="my-tickets-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <Button
              size="sm" onClick={() => navigate("/")}
              className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              data-testid="my-tickets-new-btn"
            >
              <PlusCircle className="h-4 w-4 mr-1.5" /> New request
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
          </div>
        ) : tickets.length === 0 ? (
          <Card className="p-8 text-center" data-testid="my-tickets-empty">
            <ClipboardList className="h-10 w-10 mx-auto text-[color:var(--enka-muted)]" />
            <div className="mt-3 text-sm text-[color:var(--enka-muted)]">
              You haven&apos;t submitted any requests yet.
            </div>
            <Button
              onClick={() => navigate("/")}
              className="mt-4 bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
            >
              <PlusCircle className="h-4 w-4 mr-1.5" /> Submit your first request
            </Button>
          </Card>
        ) : (
          <ul className="space-y-3">
            {tickets.map((t) => (
              <li key={t.id} data-testid={`my-ticket-${t.ticket_no}`}>
                <Card className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-[color:var(--enka-blue)]">{t.ticket_no}</div>
                      <div className="text-sm text-[color:var(--enka-ink)] mt-0.5">{t.category}</div>
                      <div className="text-xs text-[color:var(--enka-muted)] mt-0.5 truncate">
                        {t.area} · {composeLocation(t)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`enka-pill ${STATUS_STYLES[t.status] || ""}`}>{t.status}</span>
                      <span className={`enka-pill ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-[color:var(--enka-ink)] whitespace-pre-wrap line-clamp-3">
                    {t.description}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-[11px] text-[color:var(--enka-muted)]">
                      Submitted {formatAge(t.created_at)} ago · Updated {fmtDate(t.updated_at)}
                    </div>
                    {t.status === "Waiting User Confirmation" && (
                      <Button
                        size="sm" onClick={() => setConfirming(t)}
                        className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
                        data-testid={`my-ticket-confirm-btn-${t.ticket_no}`}
                      >
                        Confirm resolution
                      </Button>
                    )}
                  </div>
                  {t.admin_note && (
                    <div
                      className="mt-3 rounded-md border border-border bg-[#fafbfd] p-3 text-xs text-[color:var(--enka-ink)] whitespace-pre-wrap"
                      data-testid={`my-ticket-note-${t.ticket_no}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-[color:var(--enka-muted)] font-semibold mb-1">
                        IT note
                      </div>
                      {t.admin_note}
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>

      <ConfirmDialog
        ticket={confirming} onClose={() => setConfirming(null)}
        onConfirmed={(updated) => {
          setTickets((list) => list.map((t) => (t.id === updated.id ? updated : t)));
        }}
      />

      <footer className="text-center text-xs text-[color:var(--enka-muted)] py-4">
        IQ3B9 · IT support
      </footer>
    </div>
  );
}
